/**
 * MobileMenu
 * ---------------------------------------------------------------------------
 * Handles the full-screen mobile navigation overlay:
 *   - Open / close via hamburger button
 *   - Close via X button, Escape key, or backdrop click
 *   - Body scroll lock when open
 *   - Focus trap (Tab cycles within the menu)
 *   - ARIA attribute updates
 *   - Dispatches custom events: 'menu:open' and 'menu:close'
 *
 * Expected DOM:
 *   Trigger:  [data-mobile-menu-toggle]  (hamburger button in header)
 *   Overlay:  #MobileMenu                (the dialog wrapper)
 *   Close:    [data-mobile-menu-close]   (close button + backdrop)
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function MobileMenu() {
    this.menu = document.getElementById('MobileMenu');
    if (!this.menu) return;

    this.panel = this.menu.querySelector('.mobile-menu__panel');
    this.triggers = document.querySelectorAll('[data-mobile-menu-toggle]');
    this.closers = this.menu.querySelectorAll('[data-mobile-menu-close]');

    this.isOpen = false;
    this._previousActiveElement = null;

    this._bindEvents();
  }

  MobileMenu.prototype._bindEvents = function () {
    var self = this;

    // Open triggers (hamburger button in header)
    this.triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        self.toggle();
      });
    });

    // Close triggers (X button + backdrop)
    this.closers.forEach(function (closer) {
      closer.addEventListener('click', function (e) {
        e.preventDefault();
        self.close();
      });
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen) {
        e.preventDefault();
        self.close();
      }
    });

    // Focus trap
    this.menu.addEventListener('keydown', function (e) {
      if (e.key === 'Tab' && self.isOpen) {
        self._trapFocus(e);
      }
    });

    // Close menu when navigating (link click inside nav)
    var navLinks = this.menu.querySelectorAll('.mobile-menu__link:not(.mobile-menu__link--parent), .mobile-menu__sublink');
    navLinks.forEach(function (link) {
      link.addEventListener('click', function () {
        // Small delay so the navigation starts before closing animation
        setTimeout(function () { self.close(); }, 100);
      });
    });

    // Listen for external close request (e.g., on viewport resize to desktop)
    document.addEventListener('menu:request-close', function () {
      self.close();
    });
  };

  MobileMenu.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;

    this._previousActiveElement = document.activeElement;

    // Update ARIA
    this.menu.setAttribute('aria-hidden', 'false');
    this.triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Move focus into the menu after transition
    var self = this;
    setTimeout(function () {
      var firstFocusable = self.menu.querySelector('.mobile-menu__close');
      if (firstFocusable) firstFocusable.focus();
    }, 100);

    // Dispatch event
    document.dispatchEvent(new CustomEvent('menu:open'));
  };

  MobileMenu.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Update ARIA
    this.menu.setAttribute('aria-hidden', 'true');
    this.triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

    // Restore body scroll
    document.body.style.overflow = '';

    // Restore focus
    if (this._previousActiveElement && this._previousActiveElement.focus) {
      this._previousActiveElement.focus();
    }
    this._previousActiveElement = null;

    // Dispatch event
    document.dispatchEvent(new CustomEvent('menu:close'));
  };

  MobileMenu.prototype.toggle = function () {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  /**
   * Trap Tab / Shift+Tab focus within the menu panel.
   */
  MobileMenu.prototype._trapFocus = function (e) {
    var focusable = this.menu.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      // Shift+Tab: if on first, wrap to last
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      // Tab: if on last, wrap to first
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { new MobileMenu(); });
  } else {
    new MobileMenu();
  }
})();
